import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AccessLogRecord,
  CancellationComment,
  IdentityResponse,
  MissingFace,
  SharedTransactionDetails,
  SharedVideoLinksResponse,
  TransactionsByFaceIdResult,
} from '../types';

@Injectable()
export class PanoptycDataService {
  private httpClient = inject(HttpClient);

  apiHost = `${environment.apiBaseUrl}`;

  getIdentities(sort: string, order: string, page: number, pageSize = 50, extraParams: HttpParams): Observable<IdentityResponse> {
    const finalParams = extraParams
      .set('page', page + 1)
      .set('page_size', pageSize)
      .set('order', sort)
      .set('order_direction', order);

    return this.httpClient.get<IdentityResponse>(`${this.apiHost}/identities`, { params: finalParams });
  }

  getMissingFaces(sort: string, order: string, page: number, extraParams: string): Observable<MissingFace[]> {
    const params = this.getMissingFacesParams(sort, order, page, extraParams);
    return this.httpClient.get<MissingFace[]>(`${this.apiHost}/Uncertain/MissingFaces`, { params });
  }

  getTransactionsForIdentity(params: HttpParams): Observable<TransactionsByFaceIdResult> {
    return this.httpClient.get<TransactionsByFaceIdResult>(`${this.apiHost}/Transactions/Cancellations/FindByFace`, {
      params,
    });
  }

  updateTransactionCategory(imageId: string, category: number): Observable<unknown> {
    const requestUrl = `${this.apiHost}/transactions/${imageId}/category`;

    return this.httpClient.post<unknown>(requestUrl, { category });
  }

  getTransactionDetails(imgId: string): Observable<SharedTransactionDetails> {
    const requestUrl = `${this.apiHost}/transactions/${imgId}/details`;
    return this.httpClient.get<SharedTransactionDetails>(requestUrl);
  }

  getTransactionDetailsWithNeighbors(imgId: string): Observable<SharedTransactionDetails> {
    const requestUrl = `${this.apiHost}/transactions/${imgId}/DetailsWithNeighbors`;
    return this.httpClient.get<SharedTransactionDetails>(requestUrl);
  }

  startSharingTransaction(imgId: string): Observable<unknown> {
    const requestUrl = `${this.apiHost}/transactions/${imgId}/shared`;
    return this.httpClient.post<unknown>(requestUrl, { share: true });
  }

  stopSharingTransaction(imgId: string): Observable<unknown> {
    const requestUrl = `${this.apiHost}/transactions/${imgId}/shared`;
    return this.httpClient.post<unknown>(requestUrl, { share: false });
  }

  setTransactionViewed(imgId: string): Observable<unknown> {
    const requestUrl = `${this.apiHost}/transactions/${imgId}/viewed`;
    return this.httpClient.post<unknown>(requestUrl, {});
  }

  getTransactionComments(imgId: string): Observable<CancellationComment[]> {
    const requestUrl = `${this.apiHost}/transactions/${imgId}/comments`;
    return this.httpClient.get<CancellationComment[]>(requestUrl);
  }

  addTransactionComments(imgId: string, content: string): Observable<unknown> {
    const requestUrl = `${this.apiHost}/transactions/${imgId}/comments`;
    return this.httpClient.post<unknown>(requestUrl, { content });
  }

  getSharedVideoLinks(imgId: string): Observable<SharedVideoLinksResponse[]> {
    const requestUrl = `${this.apiHost}/transactions/${imgId}/shared_video_links`;
    return this.httpClient.get<SharedVideoLinksResponse[]>(requestUrl);
  }

  requestAccessToTheftReport(shareKey): Observable<void> {
    const requestUrl = `${this.apiHost}/TheftReport/Shared/RequestAccess`;
    return this.httpClient.post<void>(requestUrl, { share_key: shareKey });
  }

  checkAccessToTheftReport(shareKey): Observable<boolean> {
    const requestUrl = `${this.apiHost}/TheftReport/Shared/CheckAccess/${shareKey}`;
    return this.httpClient.get<boolean>(requestUrl);
  }

  private getMissingFacesParams(sort: string, order: string, page: number, extraParams: string) {
    const orderPrefix = order === 'asc' ? '' : '-';
    let params = new HttpParams({ fromObject: { page: (page + 1).toString() } });
    if (sort) {
      params = params.append('ordering', orderPrefix + sort);
    }
    if (extraParams) {
      new URLSearchParams(extraParams).forEach((value, key) => (params = params.append(key, value)));
    }
    return params;
  }

  getLocationAccessLog(branchId: number, locationId: number): Observable<AccessLogRecord[]> {
    const requestUrl = `${this.apiHost}/ReportPortal/Branches/${branchId}/Locations/${locationId}/AccessLog`;
    return this.httpClient.get<AccessLogRecord[]>(requestUrl);
  }
}
